import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSeo from "../../hooks/useSeo";
import { works } from "../../mocks/works";
import { categories } from "../../mocks/categories";
import WorkCard from "../../components/feature/WorkCard";

export default function Works() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "all";
  const query = params.get("q") ?? "";

  useSeo({
    title: "火影忍者同人作品库 - 按分类与关键词检索同人作品 | 忍界同人志",
    description:
      "浏览忍界同人志收录的火影忍者同人作品库，支持按长篇连载、热血战斗、清水治愈、穿越重生等分类筛选，也可按关键词搜索，快速找到想看的同人作品。",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "火影忍者同人作品库",
      url: "https://hhcypx.cn/works",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: works.slice(0, 8).map((w, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://hhcypx.cn/work/${w.id}`,
          name: w.title,
        })),
      },
    },
  });

  const filtered = useMemo(() => {
    let list = works;
    if (activeCat !== "all") {
      list = list.filter((w) => w.categoryId === activeCat);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (w) =>
          w.title.toLowerCase().includes(q) ||
          w.author.toLowerCase().includes(q) ||
          w.tags.some((t) => t.toLowerCase().includes(q)) ||
          w.summary.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCat, query]);

  const setCat = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("cat");
    else next.set("cat", id);
    setParams(next);
  };

  const setQuery = (v: string) => {
    const next = new URLSearchParams(params);
    if (v.trim()) next.set("q", v.trim());
    else next.delete("q");
    setParams(next);
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="relative overflow-hidden border-b border-background-300/60 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-600">作品库</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            同人作品库
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-400">
            按目录整理收录的火影忍者同人作品，从经典长篇到冷门短篇持续更新。选择分类或输入关键词，快速找到想看的同人。
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-sm">
              <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索作品名 / 作者 / 标签…"
                className="w-full rounded-md border border-background-300/70 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/60 focus:outline-none"
                aria-label="搜索同人作品"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-foreground-400">
              <i className="ri-filter-3-line" />
              找到 <span className="text-primary-300">{filtered.length}</span> 部作品
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCat("all")}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
              activeCat === "all"
                ? "bg-primary-500 text-background-50"
                : "border border-background-300/70 text-foreground-400 hover:border-primary-500/50 hover:text-primary-300"
            }`}
          >
            全部
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCat(c.id)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                activeCat === c.id
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300/70 text-foreground-400 hover:border-primary-500/50 hover:text-primary-300"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-16 flex flex-col items-center rounded-lg border border-background-300/60 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到匹配的同人作品</h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-400">
              试试更换关键词或选择其他分类，作品库每天都在持续更新。
            </p>
            <button
              type="button"
              onClick={() => {
                setCat("all");
                setQuery("");
              }}
              className="mt-5 rounded-md border border-background-300/70 px-5 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-primary-300 whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}